import type { ExportBundle } from "./protocol.js";
import type { Activity, GameState } from "./types.js";

/** Persistence used by the room. The Worker implements it on D1, the local demo in memory. */
export interface Store {
  load(): Promise<GameState | null>;
  /** Persist the new state. `prev` lets the store write only what changed into history tables. */
  save(prev: GameState | null, next: GameState): Promise<void>;
  exportAll(state: GameState): Promise<ExportBundle>;
  importAll(bundle: ExportBundle): Promise<void>;
  logUsage?(day: string, inputTokens: number, outputTokens: number): Promise<void>;
}

export function newActivities(prev: GameState | null, next: GameState): Activity[] {
  const seen = new Set(prev?.recent.map((a) => a.id) ?? []);
  return next.recent.filter((a) => !seen.has(a.id));
}

export function buildExport(
  state: GameState,
  activities: Activity[],
  extra: Partial<ExportBundle["history"]> = {},
): ExportBundle {
  return {
    format: "squire-profile",
    version: 1,
    exportedAt: new Date().toISOString(),
    state,
    history: {
      activities,
      quests: extra.quests ?? state.quests,
      reminders: extra.reminders ?? state.reminders,
      messages: extra.messages ?? state.messages,
      ratings: extra.ratings ?? state.ratings,
    },
  };
}

export function isExportBundle(v: unknown): v is ExportBundle {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  const h = o.history as Record<string, unknown> | undefined;
  return (
    o.format === "squire-profile" &&
    o.version === 1 &&
    typeof o.state === "object" &&
    o.state !== null &&
    !!h &&
    Array.isArray(h.activities)
  );
}

/** In-memory store (local demo mode, tests). History is capped. */
export class MemoryStore implements Store {
  state: GameState | null = null;
  activities: Activity[] = [];
  static readonly MAX_ACTIVITIES = 5000;

  async load(): Promise<GameState | null> {
    return this.state ? structuredClone(this.state) : null;
  }

  async save(prev: GameState | null, next: GameState): Promise<void> {
    this.activities.push(...newActivities(prev, next));
    if (this.activities.length > MemoryStore.MAX_ACTIVITIES)
      this.activities = this.activities.slice(-MemoryStore.MAX_ACTIVITIES);
    this.state = structuredClone(next);
  }

  async exportAll(state: GameState): Promise<ExportBundle> {
    return buildExport(state, [...this.activities]);
  }

  async importAll(bundle: ExportBundle): Promise<void> {
    this.state = structuredClone(bundle.state);
    this.activities = bundle.history.activities.slice(-MemoryStore.MAX_ACTIVITIES);
  }
}
